import get from 'lodash/get';
import { useLayoutEffect, useRef, useState } from 'react';
import CLASSNAMES from '../constants/CLASSNAMES';
import IDS from '../constants/IDS';
import MESSAGE_TYPES from '../constants/MESSAGE_TYPES';
import Messager from '../lib/messager';
import i18next from 'i18next';
import { META_EVENTS, SIDEBAR_EVENTS, ZOOM_EVENTS } from '../constants/CONTROLLERS_EVENT_TYPES';
import { SIDE_BAR_POSITION } from '../types/sideBar';
import { captureException } from '../helpers/sentry';
import type { IStateController } from '../lib/widget/state/state.interface';
import { useInjection } from 'inversify-react';
import { STATE_MODULE_IDS } from '../lib/widget/state/state.types';
import type { IZoomController } from '../lib/widget/zoom/zoom.interface';
import { ZOOM_MODULE_IDS } from '../lib/widget/zoom/zoom.types';
import type { IBackgroundController } from '../lib/widget/background/background.interface';
import { BACKGROUND_MODULE_IDS } from '../lib/widget/background/background.types';
import type { ITicketsController } from '../lib/widget/tickets/tickets.interface';
import { TICKETS_MODULE_IDS } from '../lib/widget/tickets/tickets.types';

const useLayout = () => {
  const stateController: IStateController = useInjection(STATE_MODULE_IDS.STATE_CONTROLLER);
  const zoomController: IZoomController = useInjection(ZOOM_MODULE_IDS.ZOOM_CONTROLLER);
  const backgroundController: IBackgroundController = useInjection(
    BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER,
  );
  const ticketsController: ITicketsController = useInjection(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);

  const { Widget } = window;
  const spinnerEl = useRef(document.getElementById(IDS.loadingSpinner));
  const [state, setState] = useState({
    isLoading: true,
    isFullscreen: false,
    showMobileOverlay: Widget.isMobile,
    orderFormIsHidden: true,
    isFailure: false,
    schemeMode: Widget.schemeMode,
    reloadOverlayVisible: false,
    isFiltersOpen: false,
    isGripperDisabled: false,
    sideBarPosition: SIDE_BAR_POSITION.MIDDLE,
  });
  const initailValues = useRef({
    isLoading: true,
    isFullscreen: false,
    showMobileOverlay: Widget.isMobile,
    orderFormIsHidden: true,
    isFailure: false,
    schemeMode: Widget.schemeMode,
    reloadOverlayVisible: false,
    sideBarPosition: SIDE_BAR_POSITION.MIDDLE,
  });

  useLayoutEffect(() => {
    ticketsController.addListener(ticketsController.events.TOGGLE_ORDER_FORM, toggleOrderForm);
    ticketsController.addListener(ticketsController.events.CHECK_ERROR, checkError);
    Widget.metaController.addListener(META_EVENTS.SELECT_DATE, selectNewDate);
    stateController.addListener(stateController.events.UPDATE, updateState);
    stateController.addListener(stateController.events.DATA_LOADED, onDataLoaded);
    if (Widget.announcementsController) {
      Widget.announcementsController.addListener(SIDEBAR_EVENTS.SET_POSITION, setSideBarPosition);
    }
    stateController.addListener(stateController.events.SCHEME_MODE_CHANGE, updateSchemeMode);
    stateController.addListener(stateController.events.WIDGET_RELOAD_TICKETS, onTicketsReload);
    window.addEventListener('message', onMessageReceived, false);
    zoomController.addListener(zoomController.events.TWO_FINGERS_ZOOM_START, disableGripper);
    zoomController.addListener(zoomController.events.TWO_FINGERS_ZOOM_END, enableGripper);

    return () => {
      ticketsController.removeListener(ticketsController.events.TOGGLE_ORDER_FORM, toggleOrderForm);
      ticketsController.removeListener(ticketsController.events.CHECK_ERROR, checkError);
      Widget.metaController.removeListener(META_EVENTS.SELECT_DATE, selectNewDate);
      stateController.removeListener(stateController.events.UPDATE, updateState);
      stateController.removeListener(stateController.events.DATA_LOADED, onDataLoaded);
      if (Widget.announcementsController) {
        Widget.announcementsController.removeListener(
          SIDEBAR_EVENTS.SET_POSITION,
          setSideBarPosition,
        );
      }
      stateController.removeListener(stateController.events.SCHEME_MODE_CHANGE, updateSchemeMode);
      stateController.removeListener(stateController.events.WIDGET_RELOAD_TICKETS, onTicketsReload);
      window.removeEventListener('message', onMessageReceived, false);
      zoomController.removeListener(zoomController.events.TWO_FINGERS_ZOOM_START, disableGripper);
      zoomController.removeListener(zoomController.events.TWO_FINGERS_ZOOM_END, enableGripper);
    };
  }, []);

  const selectNewDate = async () => {
    stateController.setState({
      isLoading: true,
      ticketsIsLoaded: false,
      isFailure: false,
    });
    setState((prevState) => ({
      ...prevState,
      isFailure: false,
    }));

    if (window.Widget.schemaIsBroken) {
      stateController.setState({
        isLoading: false,
        ticketsIsLoaded: true,
      });
      return;
    }

    ticketsController.reset();
    if (Widget.announcementsController) Widget.announcementsController.reset();
    Widget.pricesController.resetSelectedPrices();
    Widget.metaController.emit(META_EVENTS.CHANGE_SECTOR, null);

    if (state.schemeMode) {
      const { startX, startY } = Widget.schemeParameters;
      const { zoomRange } = zoomController;
      Widget.setZoomRange(zoomRange);
      const [newScale] = zoomRange;
      Widget.resetStyles();
      Widget.resetTransforms();
      Widget.translate(startX, startY);
      Widget.scale(newScale);
      Widget.update();
      zoomController.emit(ZOOM_EVENTS.UPDATE);
      backgroundController.forceUpdate();
    }

    const { pricesDates } = Widget.settings;
    if (Widget.settings.withAnnouncements && pricesDates) {
      const price = pricesDates.find((p) => p.id === Widget.metaController.selectedEvent);
      const hasAnnouncements = price && price.use_announcements;
      if (hasAnnouncements) {
        await Widget.loadAnnouncements();
      }
    }
    await ticketsController.getTickets();

    stateController.emit(stateController.events.DATA_LOADED);
    stateController.setState({
      isLoading: false,
      ticketsIsLoaded: true,
    });
    stateController.emit(stateController.events.WIDGET_RERENDER);
    stateController.emit(stateController.events.TICKETS_DATA_LOADED);
  };

  const checkError = (error) => {
    console.log(error);
    captureException(error);

    if (get(error, 'response.data.detail') === 'Currencies not supported') {
      Messager.send({
        type: MESSAGE_TYPES.ON_UNSUPPORTED_CURRENCY,
      });

      stateController.setState({
        isFailure: true,
        errorMessage: i18next.t('currencyNotSupported'),
      });

      return;
    }

    if (error.message === 'Network Error') {
      stateController.setState({
        isFailure: true,
        errorType: 'network',
      });
      return;
    }

    if (error) {
      stateController.setState({
        isFailure: true,
      });
    }

    toggleOrderForm(true);
  };

  const onDataLoaded = () => {
    spinnerEl.current?.classList.add(CLASSNAMES.HIDE);
  };

  const onTicketsReload = () => {
    spinnerEl.current?.classList.remove(CLASSNAMES.HIDE);
  };

  const updateState = (newState) => {
    const {
      isFullscreen: prevIsFullscreen,
      showMobileOverlay: prevShowMobileOverlay,
      isLoading: prevIsLoading,
      isFailure: prevIsFailure,
    } = initailValues.current;
    const { isFullscreen, showMobileOverlay, isLoading, isFailure } = newState;

    if (isLoading && spinnerEl.current) {
      spinnerEl.current?.classList.remove(CLASSNAMES.HIDE);
    }
    if (!isLoading && spinnerEl.current) {
      spinnerEl.current?.classList.add(CLASSNAMES.HIDE);
    }

    if (
      prevIsFullscreen !== isFullscreen ||
      prevShowMobileOverlay !== showMobileOverlay ||
      prevIsLoading !== isLoading ||
      prevIsFailure !== isFailure
    ) {
      initailValues.current = {
        ...initailValues.current,
        isLoading,
        isFullscreen,
        showMobileOverlay,
        isFailure,
      };

      setState((prevState) => {
        return {
          ...prevState,
          isLoading,
          isFullscreen,
          showMobileOverlay,
          isFailure,
        };
      });
    }
  };

  const updateSchemeMode = () => {
    setState((prevState) => ({
      ...prevState,
      schemeMode: Widget.schemeMode,
    }));
  };

  const hideMobileOverlayFC = () => {
    sendMessage(true);
  };

  const showMobileOverlayFC = () => {
    stateController.setState({
      showMobileOverlay: Widget.isMobile,
      isFullscreen: false,
    });

    sendMessage(false);
  };

  const onMessageReceived = (e) => {
    if (e.data.type === MESSAGE_TYPES.EXIT_FULLSCREEN) {
      showMobileOverlayFC();
    }

    if (e.data.type === MESSAGE_TYPES.HIDE_MOBILE_OVERLAY) {
      stateController.setState({
        showMobileOverlay: false,
        isFullscreen: true,
      });

      if (!stateController.state.schemeIsLoaded && !stateController.state.ticketsIsLoaded) {
        Widget.shouldRerenderOnLoadData = true;
      }
    }

    if (e.data.type === MESSAGE_TYPES.SHOW_MOBILE_OVERLAY) {
      stateController.setState({
        showMobileOverlay: true,
        isFullscreen: false,
      });
    }
  };

  const sendMessage = (value: boolean) => {
    Messager.send({
      type: MESSAGE_TYPES.FULLSCREEN,
      isFullscreen: value,
    });
  };

  const toggleOrderForm = (showForm) => {
    if (showForm) {
      stateController.setState({
        backgroundIsLoading: false,
      });

      enableScroll();
    }

    setState((prevState) => ({
      ...prevState,
      orderFormIsHidden: !showForm,
      isLoading: showForm,
      backgroundIsLoading: showForm,
    }));

    Messager.send({
      type: MESSAGE_TYPES.ON_SHOW_ORDER_FORM,
      showForm,
    });
    Messager.send({
      type: MESSAGE_TYPES.UPDATE_WIDGET_HEIGHT,
    });
  };

  const toggleReloadOverlay = (value) => {
    setState((prevState) => ({
      ...prevState,
      reloadOverlayVisible: value,
    }));
  };

  const toggleFilters = (isOpen: boolean) => {
    setState((prevState) => ({
      ...prevState,
      isFiltersOpen: isOpen,
    }));
  };

  const setSideBarPosition = (newSideBarPosition) => {
    const { sideBarPosition: prevSideBarPosition } = initailValues.current;

    stateController.state.sideBarPosition = newSideBarPosition;

    if (prevSideBarPosition !== newSideBarPosition) {
      if (newSideBarPosition !== SIDE_BAR_POSITION.TOP) {
        stateController.emit(stateController.events.WIDGET_RERENDER);
      }

      initailValues.current = {
        ...initailValues.current,
        sideBarPosition: newSideBarPosition,
      };

      setState((prevState) => ({
        ...prevState,
        sideBarPosition: newSideBarPosition,
      }));
    }
  };

  const disableGripper = () => {
    setState((prevState) => ({
      ...prevState,
      isGripperDisabled: true,
    }));
  };

  const enableGripper = () => {
    setState((prevState) => ({
      ...prevState,
      isGripperDisabled: false,
    }));
  };

  const enableScroll = () => {
    document.body.classList.remove(CLASSNAMES.SCROLL_LOCK);
    document.documentElement.classList.remove(CLASSNAMES.SCROLL_LOCK);
  };

  return {
    state,
    showMobileOverlayFC,
    hideMobileOverlayFC,
    toggleReloadOverlay,
    toggleFilters,
    setSideBarPosition,
  };
};

export default useLayout;
